import { jsx, jsxs } from "react/jsx-runtime";
import { c } from "react/compiler-runtime";
import { useTheme, styled } from "@mui/material/styles";
import ComponentWrapper from "../ComponentWrapper/ComponentWrapper.js";
import DynamicTable from "../Table/Table.js";
import Text from "../Text/Text.js";
import { useState } from "react";
import SearchBar from "../SearchBar/SearchBar.js";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
function shouldForwardProp(prop) {
  return !["withScroll"].includes(prop);
}
const Template = styled("div", {
  shouldForwardProp
})(({
  theme
}) => ({
  display: "flex",
  padding: theme.sizes.spacing.none,
  flexDirection: "column",
  flex: "1 0 0",
  width: "100%",
  alignSelf: "stretch",
  "& .card-header": {
    backgroundColor: theme.colors.surface.primary,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    alignSelf: "stretch",
    height: `${theme.sizes.table.headerHeight}rem`,
    padding: theme.sizes.spacing["06"],
    gap: theme.sizes.spacing["06"],
    borderBottom: `1px solid ${theme.colors.border.primary}`,
    "& .MuiTypography-heading-md": {
      ...theme.typography.h5,
      color: theme.colors.text.secondary
    }
  },
  "& .no-border": {
    borderBottom: "none"
  }
}));
const tabSelectMaxWidth = "20rem";
const TabSelect = styled(Select)(({
  theme
}) => ({
  "&.MuiInputBase-root": {
    width: "auto",
    minWidth: theme.sizes.spacing["15"],
    maxWidth: tabSelectMaxWidth,
    height: "auto",
    border: "none",
    backgroundColor: theme.colors.surface.primary,
    borderRadius: theme.sizes.radius.sm
  },
  "& .MuiSelect-select.MuiInputBase-input": {
    padding: `${theme.sizes.spacing["03"]} ${theme.sizes.spacing["09"]} ${theme.sizes.spacing["03"]} ${theme.sizes.spacing["04"]}`,
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.normal
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.colors.border.tertiary,
    borderWidth: "1px"
  },
  "& .MuiSelect-icon": {
    right: theme.sizes.spacing["04"],
    color: theme.colors.icon.secondary
  },
  "&:hover": {
    backgroundColor: theme.colors.surface.secondary
  },
  "&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.colors.border.focus,
    borderWidth: "1px"
  }
}));
const SummaryExtender = styled("div")(() => ({
  backgroundColor: "rgba(255, 251, 235, 1)",
  height: 0,
  flexGrow: 1
}));
const TableTemplate = t0 => {
  const $ = c(57);
  const {
    title,
    tables,
    tabTitles,
    showTabCounts,
    tabsDisplay: t1,
    isLoading: t2,
    maxHeight,
    helpContent
  } = t0;
  const tabsDisplay = t1 === void 0 ? "tabs" : t1;
  const isLoading = t2 === void 0 ? false : t2;
  let t3;
  if ($[0] !== title) {
    t3 = typeof title === "object" && title !== null ? title : {
      text: title,
      help: null
    };
    $[0] = title;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    help: t4,
    text
  } = t3;
  const help = t4 === void 0 ? null : t4;
  const [searchValue, setSearchValue] = useState("");
  const [activeTabTitle, setActiveTabTitle] = useState();
  const theme = useTheme();
  let t5;
  if ($[2] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t5 = value => {
      setSearchValue(value);
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const handleSearchChange = t5;
  let t6;
  if ($[3] !== tables) {
    t6 = tables?.some(_temp2);
    $[3] = tables;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const hasSearchableColumns = t6;
  if (!tables || tables.length === 0) {
    console.warn("[TableTemplate] No tables provided", {
      title,
      tables
    });
    return null;
  }
  if (tables.some(_temp3)) {
    console.warn("[TableTemplate] One or more tables are missing data or columns", {
      title,
      tables
    });
    return null;
  }
  const tableCount = tables.length;
  let t7;
  if ($[5] !== activeTabTitle || $[6] !== tabTitles) {
    t7 = tabTitles?.indexOf(activeTabTitle ?? "") ?? 0;
    $[5] = activeTabTitle;
    $[6] = tabTitles;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const activeTab = Math.max(t7, 0);
  let t8;
  if ($[8] !== tabTitles) {
    t8 = index => setActiveTabTitle(tabTitles?.[index]);
    $[8] = tabTitles;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleTabChange = t8;
  const showTabSwitcher = !!tabTitles && tabTitles.length === tableCount;
  let t9;
  if ($[10] !== showTabCounts || $[11] !== tables) {
    t9 = (tabTitle, index_0) => /* @__PURE__ */jsxs(Box, {
      sx: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: _temp4,
        minWidth: 0
      },
      children: [/* @__PURE__ */jsx(Box, {
        component: "span",
        sx: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        },
        children: tabTitle
      }), showTabCounts && showTabCounts[index_0] && /* @__PURE__ */jsx(Chip, {
        label: tables[index_0]?.data?.data.length,
        size: "small",
        sx: {
          flexShrink: 0
        }
      })]
    });
    $[10] = showTabCounts;
    $[11] = tables;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const renderTabLabel = t9;
  const externalHeader = !!text;
  const showDropdown = tableCount > 1 && tabsDisplay === "dropdown";
  const dropdownInHeader = showDropdown && externalHeader;
  let t10;
  if ($[13] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      slotProps: {
        paper: {
          sx: {
            maxWidth: tabSelectMaxWidth
          }
        }
      }
    };
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = showTabSwitcher ? activeTab : "";
  let t12;
  if ($[14] !== handleTabChange) {
    t12 = event => handleTabChange(Number(event.target.value));
    $[14] = handleTabChange;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== tabTitles) {
    t13 = value_0 => tabTitles?.[Number(value_0)] ?? "";
    $[16] = tabTitles;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== renderTabLabel || $[19] !== showTabSwitcher || $[20] !== tabTitles) {
    t14 = showTabSwitcher && tabTitles.map((tabTitle_0, index_1) => /* @__PURE__ */jsx(MenuItem, {
      value: index_1,
      children: renderTabLabel(tabTitle_0, index_1)
    }, index_1));
    $[18] = renderTabLabel;
    $[19] = showTabSwitcher;
    $[20] = tabTitles;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t11 || $[23] !== t12 || $[24] !== t13 || $[25] !== t14) {
    t15 = /* @__PURE__ */jsx(TabSelect, {
      size: "small",
      IconComponent: _temp5,
      MenuProps: t10,
      value: t11,
      onChange: t12,
      renderValue: t13,
      children: t14
    });
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const tabSelect = t15;
  let nonTableHeight = 0;
  if (externalHeader) {
    nonTableHeight = 0 + theme.sizes.table.headerHeight;
  }
  if (tableCount > 1 && !dropdownInHeader) {
    nonTableHeight = nonTableHeight + (showDropdown ? 3.5 : 3);
  }
  let heightInRem = theme.sizes.grid.height * 12 - nonTableHeight;
  if (maxHeight === -1) {
    heightInRem = void 0;
  } else {
    if (maxHeight !== void 0 && maxHeight > 0) {
      heightInRem = maxHeight * theme.sizes.grid.height - nonTableHeight;
    }
  }
  const maxHeightStyle = heightInRem !== void 0 ? `${heightInRem}rem` : void 0;
  let t16;
  if ($[27] !== dropdownInHeader || $[28] !== hasSearchableColumns || $[29] !== help || $[30] !== helpContent || $[31] !== searchValue || $[32] !== tabSelect || $[33] !== tableCount || $[34] !== text) {
    t16 = text && /* @__PURE__ */jsxs(ComponentWrapper, {
      className: tableCount > 1 && !dropdownInHeader ? "card-header no-border" : "card-header",
      children: [/* @__PURE__ */jsx(Text, {
        text,
        help,
        helpContent,
        component: "div",
        variant: "heading-md"
      }), /* @__PURE__ */jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          gap: _temp6,
          minWidth: 0
        },
        children: [dropdownInHeader && tabSelect, hasSearchableColumns && /* @__PURE__ */jsx(SearchBar, {
          value: searchValue,
          onChange: handleSearchChange,
          placeholder: "Search in table..."
        })]
      })]
    }, "title");
    $[27] = dropdownInHeader;
    $[28] = hasSearchableColumns;
    $[29] = help;
    $[30] = helpContent;
    $[31] = searchValue;
    $[32] = tabSelect;
    $[33] = tableCount;
    $[34] = text;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== activeTab || $[37] !== dropdownInHeader || $[38] !== externalHeader || $[39] !== handleTabChange || $[40] !== isLoading || $[41] !== maxHeightStyle || $[42] !== renderTabLabel || $[43] !== searchValue || $[44] !== showDropdown || $[45] !== showTabSwitcher || $[46] !== tabSelect || $[47] !== tabTitles || $[48] !== tables) {
    t17 = /* @__PURE__ */jsx(ComponentWrapper, {
      className: "table-container",
      children: tables.length === 1 && tables[0] ? /* @__PURE__ */jsx(DynamicTable, {
        maxHeight: maxHeightStyle,
        data: tables[0].data,
        columns: tables[0].columns,
        summary: tables[0].summary,
        isLoading,
        externalHeader,
        searchValue,
        defaultSortBy: tables[0].defaultSortBy
      }) : /* @__PURE__ */jsxs(Box, {
        children: [showDropdown && !dropdownInHeader && /* @__PURE__ */jsx(Box, {
          sx: {
            paddingX: _temp7,
            paddingY: _temp8,
            borderBottom: _temp9
          },
          children: tabSelect
        }), !showDropdown && /* @__PURE__ */jsx(Box, {
          sx: {
            paddingLeft: _temp0,
            borderBottom: _temp1,
            marginTop: "-1px"
          },
          children: /* @__PURE__ */jsx(Tabs, {
            value: activeTab,
            onChange: (_, newValue) => handleTabChange(newValue),
            children: showTabSwitcher && tabTitles.map((tabTitle_1, index_2) => /* @__PURE__ */jsx(Tab, {
              label: renderTabLabel(tabTitle_1, index_2),
              value: index_2,
              sx: {
                textTransform: "none"
              }
            }, index_2))
          })
        }), activeTab >= 0 && tables[activeTab] && /* @__PURE__ */jsx(Box, {
          sx: {
            maxHeight: maxHeightStyle
          },
          children: /* @__PURE__ */jsx(DynamicTable, {
            maxHeight: maxHeightStyle,
            data: tables[activeTab].data,
            columns: tables[activeTab].columns,
            summary: tables[activeTab].summary,
            isLoading,
            externalHeader,
            searchValue,
            defaultSortBy: tables[activeTab].defaultSortBy
          })
        })]
      })
    }, "trend-indicator");
    $[36] = activeTab;
    $[37] = dropdownInHeader;
    $[38] = externalHeader;
    $[39] = handleTabChange;
    $[40] = isLoading;
    $[41] = maxHeightStyle;
    $[42] = renderTabLabel;
    $[43] = searchValue;
    $[44] = showDropdown;
    $[45] = showTabSwitcher;
    $[46] = tabSelect;
    $[47] = tabTitles;
    $[48] = tables;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== activeTab || $[51] !== tables) {
    t18 = !!tables[activeTab]?.summary && /* @__PURE__ */jsx(SummaryExtender, {});
    $[50] = activeTab;
    $[51] = tables;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== t16 || $[54] !== t17 || $[55] !== t18) {
    t19 = /* @__PURE__ */jsxs(Template, {
      children: [t16, t17, t18]
    });
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  return t19;
};
function _temp(col) {
  return col.searchable === true;
}
function _temp2(table) {
  return table.columns?.some(_temp);
}
function _temp3(table_0) {
  return !table_0.data || !table_0.columns;
}
function _temp4(theme_0) {
  return theme_0.sizes.spacing["04"];
}
function _temp5(props) {
  return /* @__PURE__ */jsx(FontAwesomeIcon, {
    ...props,
    icon: faChevronDown,
    size: "xs"
  });
}
function _temp6(theme_1) {
  return theme_1.sizes.spacing["04"];
}
function _temp7(theme_2) {
  return theme_2.sizes.spacing["06"];
}
function _temp8(theme_3) {
  return theme_3.sizes.spacing["04"];
}
function _temp9(theme_4) {
  return `1px solid ${theme_4.colors.border.primary}`;
}
function _temp0(theme_5) {
  return theme_5.sizes.spacing["06"];
}
function _temp1(theme_6) {
  return `1px solid ${theme_6.colors.border.primary}`;
}
export { TableTemplate as default };